import { queryOptions } from '@tanstack/react-query';
import {
  CrmBoardDto,
  type CrmBoardQuery,
  CrmBoardStageDealsDto,
  type CrmBoardStageDealsQuery,
  CrmCitiesDto,
  CrmContactContextDto,
  CrmConversationMessagesDto,
  CrmConversationsListDto,
  type CrmConversationsQuery,
  CrmEventsDto,
  CrmManagerEventInterestsDto,
  CrmMessagingChannelsDto,
  CrmPartyMergePreviewDto,
  CrmPartyResolveContactDto,
  type CrmPartyResolveContactQuery,
  CrmPartySearchResultDto,
  CrmPartyUnifiedPanelDto,
  CrmSuggestionsDto,
} from '@upme/contracts';
import { api } from '../../app/api-client';

/** Сколько карточек отдаёт первый запрос доски на колонку. */
export const CRM_BOARD_INITIAL_STAGE_LIMIT = 10;

export function normalizeCrmBoardQuery(query: CrmBoardQuery = { segment: 'all' }): CrmBoardQuery {
  return {
    segment: query.segment ?? 'all',
    stageLimit: query.stageLimit ?? CRM_BOARD_INITIAL_STAGE_LIMIT,
    ...(query.cityId ? { cityId: query.cityId } : {}),
    ...(query.partnershipStatus ? { partnershipStatus: query.partnershipStatus } : {}),
  };
}

export const crmBoardKey = (query: CrmBoardQuery = { segment: 'all' }) =>
  ['admin', 'crm', 'board', normalizeCrmBoardQuery(query)] as const;

function boardSearchParams(query: CrmBoardQuery): Record<string, string> {
  const normalized = normalizeCrmBoardQuery(query);
  const params: Record<string, string> = {
    segment: normalized.segment,
    stageLimit: String(normalized.stageLimit ?? CRM_BOARD_INITIAL_STAGE_LIMIT),
  };
  if (normalized.cityId) params.cityId = normalized.cityId;
  if (normalized.partnershipStatus) params.partnershipStatus = normalized.partnershipStatus;
  return params;
}

function stageDealsSearchParams(query: CrmBoardStageDealsQuery): Record<string, string> {
  const { stage, offset, limit, ...boardRest } = query;
  return {
    ...boardSearchParams({ ...boardRest, stageLimit: 0 }),
    stage,
    offset: String(offset ?? 0),
    limit: String(limit ?? CRM_BOARD_INITIAL_STAGE_LIMIT),
  };
}

export const crmBoardOptions = (query: CrmBoardQuery = { segment: 'all' }) =>
  queryOptions({
    queryKey: crmBoardKey(query),
    queryFn: () =>
      api
        .get('admin/crm/board', { searchParams: boardSearchParams(query) })
        .json<CrmBoardDto>()
        .then((data) => CrmBoardDto.parse(data)),
  });

export const crmBoardStageDealsKey = (query: CrmBoardStageDealsQuery) =>
  ['admin', 'crm', 'board', 'stage-deals', query] as const;

export const crmBoardStageDealsOptions = (query: CrmBoardStageDealsQuery) =>
  queryOptions({
    queryKey: crmBoardStageDealsKey(query),
    queryFn: () =>
      api
        .get('admin/crm/board/stage-deals', { searchParams: stageDealsSearchParams(query) })
        .json()
        .then((data) => CrmBoardStageDealsDto.parse(data)),
  });

export const crmManagerEventInterestsKey = (contactUserId: string) =>
  ['admin', 'crm', 'contacts', contactUserId, 'manager-event-interests'] as const;

export const crmManagerEventInterestsOptions = (contactUserId: string) =>
  queryOptions({
    queryKey: crmManagerEventInterestsKey(contactUserId),
    queryFn: () =>
      api
        .get(`admin/crm/contacts/${contactUserId}/manager-event-interests`)
        .json()
        .then((data) => CrmManagerEventInterestsDto.parse(data)),
    enabled: Boolean(contactUserId),
  });

export const crmContactContextKey = (contactUserId: string) =>
  ['admin', 'crm', 'contacts', contactUserId, 'context'] as const;

export const crmContactContextOptions = (contactUserId: string) =>
  queryOptions({
    queryKey: crmContactContextKey(contactUserId),
    queryFn: () =>
      api
        .get(`admin/crm/contacts/${contactUserId}/context`)
        .json()
        .then((data) => CrmContactContextDto.parse(data)),
    enabled: Boolean(contactUserId),
  });

export const crmEventsOptions = (cityId?: string) =>
  queryOptions({
    queryKey: ['admin', 'crm', 'events', cityId ?? null],
    queryFn: () =>
      api
        .get('admin/crm/events', { searchParams: cityId ? { cityId } : {} })
        .json()
        .then(CrmEventsDto.parse),
  });

export const crmCitiesOptions = () =>
  queryOptions({
    queryKey: ['admin', 'crm', 'cities'],
    queryFn: () => api.get('admin/crm/cities').json().then(CrmCitiesDto.parse),
    staleTime: 30_000,
  });

/** Подсказки персистентны на менеджера и не зависят от фильтров — фильтры лишь выбирают, откуда добирать слоты. */
export const crmSuggestionsKey = () => ['admin', 'crm', 'suggestions'] as const;

export const crmSuggestionsOptions = (query: CrmBoardQuery = { segment: 'all' }) =>
  queryOptions({
    queryKey: crmSuggestionsKey(),
    queryFn: () =>
      api
        .get('admin/crm/suggestions', { searchParams: boardSearchParams(query) })
        .json()
        .then((data) => CrmSuggestionsDto.parse(data)),
  });

function conversationsSearchParams(query: CrmConversationsQuery): Record<string, string> {
  const params: Record<string, string> = { limit: String(query.limit ?? 50) };
  if (query.channel) params.channel = query.channel;
  if (query.cityId) params.cityId = query.cityId;
  if (query.assigneeUserId) params.assigneeUserId = query.assigneeUserId;
  if (query.q) params.q = query.q;
  if (query.cursor) params.cursor = query.cursor;
  return params;
}

export const crmMessagingChannelsOptions = () =>
  queryOptions({
    queryKey: ['admin', 'crm', 'messaging', 'channels'],
    queryFn: () =>
      api
        .get('admin/crm/messaging/channels')
        .json()
        .then((data) => CrmMessagingChannelsDto.parse(data)),
    staleTime: 60_000,
  });

export const crmConversationsOptions = (query: CrmConversationsQuery) =>
  queryOptions({
    queryKey: ['admin', 'crm', 'conversations', query] as const,
    queryFn: () =>
      api
        .get('admin/crm/conversations', { searchParams: conversationsSearchParams(query) })
        .json()
        .then((data) => CrmConversationsListDto.parse(data)),
  });

export const crmPartyUnifiedPanelOptions = (partyId: string) =>
  queryOptions({
    queryKey: ['admin', 'crm', 'parties', 'unified-panel', partyId] as const,
    queryFn: () =>
      api
        .get(`admin/crm/parties/${partyId}/unified-panel`)
        .json()
        .then((data) => CrmPartyUnifiedPanelDto.parse(data)),
    enabled: Boolean(partyId),
  });

export const crmResolvePartyOptions = (params: CrmPartyResolveContactQuery) =>
  queryOptions({
    queryKey: ['admin', 'crm', 'parties', 'resolve', params] as const,
    queryFn: () =>
      api
        .get('admin/crm/parties/resolve-contact', {
          searchParams: {
            inboundChannel: params.inboundChannel,
            contactUserId: params.contactUserId,
            ...(params.manyChatContactId ? { manyChatContactId: params.manyChatContactId } : {}),
          },
        })
        .json()
        .then((data) => CrmPartyResolveContactDto.parse(data)),
  });

export const crmPartySearchOptions = (q: string, excludePartyId?: string) =>
  queryOptions({
    queryKey: ['admin', 'crm', 'parties', 'search', q, excludePartyId ?? null] as const,
    queryFn: () =>
      api
        .get('admin/crm/parties/search', {
          searchParams: {
            q,
            ...(excludePartyId ? { excludePartyId } : {}),
          },
        })
        .json()
        .then((data) => CrmPartySearchResultDto.parse(data)),
    enabled: q.trim().length >= 2,
  });

export const crmMergePreviewOptions = (survivorPartyId: string, mergedPartyId: string) =>
  queryOptions({
    queryKey: ['admin', 'crm', 'parties', 'merge-preview', survivorPartyId, mergedPartyId] as const,
    queryFn: () =>
      api
        .get('admin/crm/parties/merge-preview', {
          searchParams: { survivorPartyId, mergedPartyId },
        })
        .json()
        .then((data) => CrmPartyMergePreviewDto.parse(data)),
    enabled: Boolean(survivorPartyId && mergedPartyId && survivorPartyId !== mergedPartyId),
  });

export const crmConversationMessagesOptions = (conversationId: string) =>
  queryOptions({
    queryKey: ['admin', 'crm', 'conversations', conversationId, 'messages'] as const,
    queryFn: () =>
      api
        .get(`admin/crm/conversations/${conversationId}/messages`)
        .json()
        .then((data) => CrmConversationMessagesDto.parse(data)),
    enabled: Boolean(conversationId),
  });
